import { State, Action, StateContext, Selector, Store, createSelector } from '@ngxs/store';
import { insertItem, patch } from '@ngxs/store/operators';
import { BorrowerValueService } from 'app/services/borrower-value/borrower-value.service';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { ClearBorrowerValues,
  GetBorrowerValues,
  SaveBorrowerValues,
  SubscribeBorrowerValues,
  UnsubscribeBorrowerValues,
  DeleteBorrowerValueByAlias,
  GetImportantBorrowerValuesSourceHistory
} from './borrower-value.actions';
import {catchError, tap} from 'rxjs/operators';
import _ from 'lodash';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import {of} from 'rxjs';

export class BorrowerValueStateModel {
  borrowerId: null | number;
  loading: boolean;
  borrowerValues: BorrowerValue[];
  borrowerValuesWithSourceHistory: BorrowerValue[];
}

@State<BorrowerValueStateModel>({
  name: 'borrowerValues',
	defaults: {
    borrowerId: null,
    loading: true,
    borrowerValues: [],
    borrowerValuesWithSourceHistory: []
	}
})

@Injectable()

export class BorrowerValueState {

  @Selector()
	static blastValue(state: BorrowerValueStateModel) {
    return state.borrowerValues.find(item => item.alias === 'blastInfo');
  }

  @Selector()
	static borrowerValues(state: BorrowerValueStateModel) {
    return state.borrowerValues;
  }

  @Selector()
	static loading(state: BorrowerValueStateModel) {
    return state.loading;
  }

  @Selector()
  static valueByAlias(state: BorrowerValueStateModel) {
    return (alias: string) => {
      const borrowerValue = state?.borrowerValues && state.borrowerValues.constructor === Array
        ? state.borrowerValues.find(bv => bv.alias === alias)
        : null;
      return borrowerValue ? borrowerValue : null;
    };
  }

  @Selector()
  static valueSelectorByAlias(alias: string) {
    return createSelector([BorrowerValueState], state => state.borrowerValues.borrowerValues.filter(bv => alias === bv.alias)[0]);
  }

  @Selector()
  static complianceReview(state: BorrowerValueStateModel): boolean {
    const borrowerValue = state.borrowerValues.find(bv => bv.alias === 'complianceReview');
    return !!(borrowerValue && borrowerValue.value !== null);
  }

  @Selector()
  static mappedProfileByAliases(aliases: Array<string>) {
    return createSelector([BorrowerValueState], state => {
      const vals = state.borrowerValues.borrowerValues.filter(bv => aliases.includes(bv.alias));
      return _.keyBy(vals, 'alias');
    });
  }

  @Selector()
  static borrowerValuesWithSourceHistory(state: BorrowerValueStateModel): BorrowerValue[] {
    return state.borrowerValuesWithSourceHistory;
  }

  private pusherChannel: any|null = null;

  constructor(
    private borrowerValueService: BorrowerValueService,
    private pusherService: PusherService,
    private store: Store
  ) {}

  @Action(SubscribeBorrowerValues)
  subscribeBorrowerValues({ getState, patchState }: StateContext<BorrowerValueStateModel>, { borrowerId }: SubscribeBorrowerValues) {
    const prevBorrowerId = getState().borrowerId;

    if (borrowerId === prevBorrowerId) {
      return;
    }

    if (prevBorrowerId) {
      this.pusherUnsubscribe(prevBorrowerId);
    }

    patchState({
      borrowerId,
      borrowerValues: [],
      borrowerValuesWithSourceHistory: [],
      loading: true
    });

    this.store.dispatch(new GetBorrowerValues(borrowerId));
    this.pusherSubscribe({ getState, patchState }, borrowerId);
  }

  @Action(UnsubscribeBorrowerValues)
  unsubscribeBorrowerValues(context: StateContext<BorrowerValueStateModel>, { borrowerId }: SubscribeBorrowerValues) {
    this.store.dispatch(new ClearBorrowerValues(borrowerId));
  }

  @Action(GetBorrowerValues, { cancelUncompleted: true })
  getBorrowerValues({ getState, patchState }: StateContext<BorrowerValueStateModel>, { borrowerId }: GetBorrowerValues) {
    const prevBorrowerId = getState().borrowerId;

    if (borrowerId !== prevBorrowerId) {
      // unsub previous borrower
      if (prevBorrowerId) {
        this.pusherUnsubscribe(prevBorrowerId);
      }

      // reset state
      patchState({
        borrowerId,
        borrowerValues: [],
        loading: true
      });
    } else {
      patchState({
        loading: true
      });
    }

    return this.borrowerValueService
      .forBorrower(borrowerId)
      .pipe(
        tap(borrowerValues => {
          patchState({ borrowerValues, loading: false });
        }),
        catchError( error => {
          console.error(error);
          return of();
        }),
      );
  }

  @Action(ClearBorrowerValues)
	clearBorrowerValues({ getState, patchState }: StateContext<BorrowerValueStateModel>, { borrowerId }: ClearBorrowerValues) {
    const prevBorrowerId = getState().borrowerId;

    if (borrowerId !== prevBorrowerId) {
      return;
    }

    this.pusherUnsubscribe(borrowerId);
    patchState({
      borrowerId: null,
      borrowerValues: [],
      loading: true,
    });
  }

  @Action(SaveBorrowerValues)
	saveBorrowerValues({ patchState, getState }: StateContext<BorrowerValueStateModel>,
    { borrowerId, borrowerValues }: SaveBorrowerValues) {
    patchState({ loading: true });
    return this.borrowerValueService.save(borrowerId, borrowerValues)
      .pipe(
        tap(updatedBorrowerValues => {
          patchState({
            borrowerValues: this.mergeBorrowerValues(
              getState().borrowerValues,
              updatedBorrowerValues
            ),
            loading: false
          });
        }),
        catchError( error => {
          console.error(error);
          return of();
        }),
    );
  }

  @Action(DeleteBorrowerValueByAlias)
  deleteBorrowerValueByAlias({ patchState, getState }: StateContext<BorrowerValueStateModel>,
    { borrowerId, alias }: DeleteBorrowerValueByAlias) {
    patchState({ loading: true });
    return this.borrowerValueService.deleteByAlias(borrowerId, alias)
      .pipe(
        tap(deletedBorrowerValue => {
          if (!!deletedBorrowerValue) {
            const updatedBorrowerValue = getState().borrowerValues.filter(bv => bv.alias === alias);
            updatedBorrowerValue[0].value = null;
            patchState({
              borrowerValues: this.mergeBorrowerValues(
                getState().borrowerValues,
                updatedBorrowerValue
              ),
              loading: false
            });
          } else {
            patchState({ loading: false });
          }
        }),
        catchError( error => {
          console.error(error);
          return of();
        }),
    );
  }

  @Action(GetImportantBorrowerValuesSourceHistory)
  getImportantBorrowerValuesSourceHistory(
    ctx: StateContext<BorrowerValueStateModel>,
    { borrowerId, attributeId }: GetImportantBorrowerValuesSourceHistory
  ) {
    return this.borrowerValueService.getBvForAttributeWithAllSources(borrowerId, attributeId).pipe(
      tap(borrowerValuesWithSourceHistory => {
        const current = ctx.getState().borrowerValuesWithSourceHistory,
          newValues = borrowerValuesWithSourceHistory.filter(val => !current.find(prev => prev.id === val.id));
        ctx.setState(patch({
          borrowerValuesWithSourceHistory: [
            ...current,
            ...newValues,
          ],
        }));
      })
    );
  }

  private pusherSubscribe({ patchState, getState }, borrowerId: number) {
    this.pusherChannel = this.pusherService
      .subscribeRaw(this.channelName(borrowerId));

    this.pusherChannel.bind_global((eventName, data) => {
        const existing = getState().borrowerValues;
        const updated: BorrowerValue[] = Object.values(_(data).get('object.changes', {}));

        // handle only recognizable pusher messages
        if (
          eventName !== 'update'
          || _(data).get('resourceName') !== 'borrower-profile'
          || !updated.length
        ) {
          return;
        }

        patchState({
          borrowerValues: this.mergeBorrowerValues(
            existing,
            updated
          ),
          loading: false
        });
      });
  }

  private pusherUnsubscribe(borrowerId: number) {
    this.pusherService.unsubscribeRaw(this.channelName(borrowerId));
    this.pusherChannel = null;
  }

  private mergeBorrowerValues(existing: BorrowerValue[], updated: BorrowerValue[]) {
    // index existing and updated by alias
    // merge updated onto existing
    // throw away indexes, keep the array of values
    return Object.values(Object.assign({},
      _.keyBy(existing, 'alias'),
      _.keyBy(updated, 'alias')
    ));
  }

  private channelName(borrowerId: number): string {
    return `private-${environment.environment}-borrower-${borrowerId}-profile-channel`;
  }
}
